[
  {
    "id": "isuzu-box-truck-body-repair-guide",
    "path": "/isuzu-box-truck-body-repair-guide",
    "kind": "guide",
    "navLabel": "Isuzu Body Repair Guide",
    "eyebrow": "Isuzu truck guide",
    "title": "Isuzu Box Truck Body Repair Guide | Motor City Truck Collision",
    "description": "Plan Isuzu box truck body repairs with details about cab damage, cargo boxes, roofs, doors, floors and paint. Learn what to send the Brighton shop.",
    "h1": "Isuzu Box Truck Body Repair Guide",
    "intro": "An Isuzu truck body repair request can involve the cab, the cargo box or both. This guide helps you describe the damage and the way the truck is used before discussing bodywork with Motor City Truck Collision in Brighton.",
    "answer": {
      "question": "What should an Isuzu box truck body repair assessment cover?",
      "text": "Describe the affected cab panels, cargo-box walls, roof, corners, floor and doors, along with paint or liftgate-related body damage. Include the truck model, box type and how the damage affects daily use so the repair discussion covers the complete job."
    },
    "sections": [
      {
        "heading": "Separate cab damage from cargo-box damage",
        "paragraphs": [
          "Cab repairs may involve the cab front panels, bumper, doors, steps, mirrors, grille or surrounding panels. Cargo-box work can involve roof seams, walls, corners, flooring and door hardware. Explain whether the problem is in one area or crosses several connected parts."
        ]
      },
      {
        "heading": "Explain how the truck works",
        "paragraphs": [
          "A delivery box, landscape body and contractor service body have different working needs. Mention doors that no longer operate normally, water entering the box, damaged lining or tie-down areas, and bodywork around the liftgate. These details help focus the assessment."
        ]
      },
      {
        "heading": "Include the finish and fleet requirements",
        "paragraphs": [
          "Tell the shop whether repaired panels need to match the existing truck or other fleet vehicles. A paint code, unit number and useful history of earlier body repairs can support the discussion. Painting inquiries are welcome even when there is no collision damage."
        ]
      }
    ],
    "checklist": {
      "heading": "Details to have ready",
      "items": [
        "Truck year and model, plus the cargo-box or service-body type.",
        "Damage locations and any change in door or cargo-box function.",
        "Optional wide photos and close-ups of the affected areas.",
        "Fleet unit ID, color details and preferred return date."
      ]
    },
    "faqs": [
      {
        "question": "Is the Isuzu cab and cargo box the same repair job?",
        "answer": "They can be part of one inquiry, but describe each separately. Cab components and cargo-box walls, roofs, doors and floors require their own assessment."
      },
      {
        "question": "What if the roof leaks but the outside damage looks small?",
        "answer": "Report where and when water appears and any known impact. Photos can help start the conversation; the repair scope needs an assessment of the affected area."
      },
      {
        "question": "Can I ask about the liftgate?",
        "answer": "Yes. Describe damage around its pocket, rear frame or door interface. Hydraulic or mechanical liftgate problems need the appropriate service contact."
      }
    ],
    "related": [
      {
        "path": "/Isuzu-Repair",
        "label": "Isuzu body repair"
      },
      {
        "path": "/box-truck-repair.html",
        "label": "Box truck body repair"
      },
      {
        "path": "/Fleet-Truck-Body-Repair",
        "label": "Fleet repair planning"
      }
    ],
    "sourceUrls": [
      "https://www.motorcitytruckcollision.com/isuzu-box-truck-body-repair-guide",
      "https://www.motorcitytruckcollision.com/Isuzu-Repair",
      "https://www.motorcitytruckcollision.com/box-truck-repair.html",
      "https://www.motorcitytruckcollision.com/Lift-Gate-Repair"
    ],
    "preservedFacts": [
      "Isuzu cab, box, roof, sidewall, corner, floor, door, latch and roll-up door topics retained.",
      "Leaks, seams, trim, finish matching and liftgate-related body damage retained.",
      "Year/model, body type, photos, operating use and fleet timing remain useful intake details.",
      "No engine-repair, hydraulic-rebuild, roadworthiness or certification claim."
    ]
  },
  {
    "id": "rv-body-roof-repair-guide",
    "path": "/rv-body-roof-repair-guide",
    "kind": "guide",
    "navLabel": "RV Body & Roof Guide",
    "eyebrow": "RV exterior guide",
    "title": "RV Body and Roof Repair Guide | Motor City Truck Collision",
    "description": "Understand RV body and roof repair concerns, including leaks, fiberglass, seams, panels and exterior damage. Prepare details for an assessment in Brighton.",
    "h1": "RV Body and Roof Repair Guide",
    "intro": "A roof leak, cracked fiberglass panel or damaged RV corner deserves more than a quick description of the visible spot. This guide helps you organize exterior and water-entry concerns before talking with Motor City Truck Collision in Brighton.",
    "answer": {
      "question": "What information helps with an RV body or roof repair inquiry?",
      "text": "Describe the damaged area, any known impact, where water appears and when you first noticed it. Include the RV year, model and body material if known. Exterior and interior photos can help explain the concern, but they are optional."
    },
    "sections": [
      {
        "heading": "Describe the exterior opening or damage",
        "paragraphs": [
          "Useful details include roof seams and edges, front or rear caps, sidewalls, corners, trim, access doors and compartments. Mention cracked fiberglass, damaged panels or fittings that changed after an impact. Several connected areas may need to be reviewed together."
        ]
      },
      {
        "heading": "Record the water-entry pattern",
        "paragraphs": [
          "Note whether staining or moisture appears while the RV is parked, during rain or after travel. The place water becomes visible may not describe the full repair area. An assessment can help establish which roof, seam or body components need attention."
        ]
      },
      {
        "heading": "Plan around travel and storage",
        "paragraphs": [
          "Share your preferred repair window and upcoming travel or storage plans. Explain any previous roof or body repair you know about. This helps the shop discuss the scope and scheduling; it does not establish a completion date before the work is reviewed."
        ]
      }
    ],
    "checklist": {
      "heading": "Prepare an RV exterior repair inquiry",
      "items": [
        "Year, make, model and whether it is a motorhome or towable RV.",
        "Affected roof, cap, wall, compartment or trim locations.",
        "When leaks or damage appeared, including any known impact.",
        "Optional exterior photos and pictures of visible water staining.",
        "Preferred travel or storage dates."
      ]
    },
    "faqs": [
      {
        "question": "Does RV roof repair always mean a replacement roof?",
        "answer": "The affected material and extent of damage need assessment. Describe the concern first rather than assuming a patch or complete replacement will be appropriate."
      },
      {
        "question": "Can body damage affect doors or compartments?",
        "answer": "Yes. Include any change in fit or operation when describing the impact, along with the surrounding panel, trim or corner damage."
      },
      {
        "question": "Does this guide cover appliance repairs?",
        "answer": "It focuses on exterior body, roof, fiberglass and paint concerns. For appliances, plumbing or other interior systems, confirm the appropriate RV service provider before arranging work."
      }
    ],
    "related": [
      {
        "path": "/RV-Repair",
        "label": "RV exterior repair"
      },
      {
        "path": "/Complete-RV-Trailer-Repair-Maintenance",
        "label": "RV trailer repair planning"
      },
      {
        "path": "/Commercial-Truck-Paint-Shop",
        "label": "Exterior painting and refinishing"
      }
    ],
    "sourceUrls": [
      "https://www.motorcitytruckcollision.com/rv-body-roof-repair-guide",
      "https://www.motorcitytruckcollision.com/RV-Repair",
      "https://www.motorcitytruckcollision.com/Complete-RV-Trailer-Repair-Maintenance"
    ],
    "preservedFacts": [
      "Roof leaks, seam and cap damage, sidewalls, corners, compartments, trim, lights and exterior finish topics retained.",
      "Timing and location of water appearance, previous impact and travel planning retained.",
      "Fiberglass, roof, body and paint scope retained without promising appliance, plumbing or interior-system services."
    ]
  },
  {
    "id": "identifying-common-paint-defects",
    "path": "/Identifying-Common-Paint-Defects",
    "kind": "guide",
    "navLabel": "Paint Defect Guide",
    "eyebrow": "Paint and finish guide",
    "title": "Identifying Common Paint Defects | Motor City Truck Collision",
    "description": "Recognize paint symptoms such as peeling, blistering, poor color match, runs and uneven texture. Learn what information helps a professional finish assessment.",
    "h1": "Identifying Common Paint Defects",
    "intro": "Paint problems can look similar while needing different repairs. This guide helps you describe the finish you see, using familiar defect terms from PPG's paint resources, before discussing the affected vehicle or equipment with Motor City Truck Collision.",
    "answer": {
      "question": "Can the appearance of a paint defect tell you how to fix it?",
      "text": "Appearance is a useful starting point, but it does not establish the cause or the repair method. The shop needs to consider the existing coating, surface condition, affected area and repair history before discussing whether cleaning, polishing or refinishing is appropriate."
    },
    "sections": [
      {
        "heading": "Start with the visible symptom",
        "paragraphs": [
          "Peeling or flaking describes coating that is separating. Blistering appears as raised bubbles. Runs or sags show excess coating in an area, while orange peel describes an uneven texture. A color difference or loss of gloss is another useful symptom to report."
        ]
      },
      {
        "heading": "Include the history, not just a close-up",
        "paragraphs": [
          "Explain when the problem appeared, whether the panel was previously painted and whether one spot or several panels are affected. Mention known exposure to contaminants or damage. A wide view shows the location; a closer image can show texture or the edges of the defect."
        ]
      },
      {
        "heading": "Discuss the repair before choosing a remedy",
        "paragraphs": [
          "A surface mark and a failing coating are different concerns. Avoid choosing a repair solely from an online photo match. PPG's technical resources address defect identification and corrective processes; product-specific procedures belong with the professional assessment and current manufacturer information."
        ]
      }
    ],
    "checklist": {
      "heading": "Information for a paint assessment",
      "items": [
        "Vehicle or equipment type and the affected panels.",
        "When the symptom appeared and any known repaint history.",
        "Whether the concern is texture, color, gloss, peeling or blistering.",
        "Optional wide photos and close-ups in useful lighting.",
        "The finish you want to retain or achieve."
      ]
    },
    "faqs": [
      {
        "question": "Can every paint defect be polished out?",
        "answer": "No single remedy fits every symptom. The depth and condition of the coating need review before the shop recommends polishing or refinishing."
      },
      {
        "question": "Does a poor color match mean the whole vehicle needs paint?",
        "answer": "The affected area and matching requirements need assessment. Include which panels differ and any paint code or fleet color information you have."
      },
      {
        "question": "Should I identify the exact defect before calling?",
        "answer": "No. Describe what you see in plain language. The familiar terms on this page can help, but a photo or description is not a final diagnosis."
      }
    ],
    "note": {
      "heading": "PPG reference resources",
      "text": "PPG publishes guidance on common paint defects and refinishing. Use its current technical resources for manufacturer information; this page is an original overview for repair inquiries.",
      "href": "https://www.ppg.com/en-US/refinish/support/user-guides-and-manuals",
      "linkLabel": "View PPG user guides and manuals"
    },
    "related": [
      {
        "path": "/Commercial-Truck-Paint-Shop",
        "label": "Commercial painting"
      },
      {
        "path": "/PPG-Custom-Auto-Restoration-Guide",
        "label": "Restoration planning guide"
      },
      {
        "path": "/Rust-Prevention-How-You-Can-Slow-Down-Rust-On-Your-Vehicle",
        "label": "Rust and finish care"
      }
    ],
    "sourceUrls": [
      "https://www.motorcitytruckcollision.com/Identifying-Common-Paint-Defects",
      "https://www.ppg.com/en-US/refinish/support/user-guides-and-manuals",
      "https://www.motorcitytruckcollision.com/Commercial-Truck-Paint-Shop"
    ],
    "preservedFacts": [
      "Common defect identification, including adhesion loss, blistering, peeling, runs, orange peel, poor color match and gloss retained.",
      "PPG attribution retained with a direct current primary-resource link.",
      "Original overview replaces lengthy reproduced manual text and absolute finish guarantees.",
      "No product ratios, chemical application procedures, warranty or manufacturer certification claim."
    ]
  },
  {
    "id": "ppg-custom-auto-restoration-guide",
    "path": "/PPG-Custom-Auto-Restoration-Guide",
    "kind": "guide",
    "navLabel": "Restoration Planning Guide",
    "eyebrow": "Restoration reference",
    "title": "PPG Custom Restoration Guide | Motor City Truck Collision",
    "description": "An original overview of PPG restoration planning topics: existing finishes, surface preparation, color decisions and project details. Links to PPG's official guide.",
    "h1": "PPG Custom Restoration Guide",
    "intro": "PPG's Custom Restoration Guide is a professional reference for preparing and painting restoration projects. This original overview explains how those planning topics can help you describe a body and paint project to Motor City Truck Collision.",
    "answer": {
      "question": "What should be settled before a restoration paint project begins?",
      "text": "Start with the vehicle or equipment, its existing finish and the work you want completed. Discuss body repairs, preparation, the desired color and finish, and the areas included in the job. Technical product choices need current manufacturer guidance and a project assessment."
    },
    "sections": [
      {
        "heading": "Understand the starting surface",
        "paragraphs": [
          "PPG’s Custom Restoration Guide covers preparation for metal and plastic parts, coated surfaces and stripped surfaces. Explain what has already been repaired, stripped, blasted or painted, and mention any unknown coating history."
        ]
      },
      {
        "heading": "Describe the finished result you want",
        "paragraphs": [
          "Say whether the project is a repair refinish, color change or restoration of an older appearance. Bring useful color references or a paint code if you have one. Explain whether matching nearby panels or other fleet equipment matters, and identify areas you want included."
        ]
      },
      {
        "heading": "Use the manufacturer reference appropriately",
        "paragraphs": [
          "PPG's guide organizes professional preparation and painting processes, and directs users to the relevant product data sheets. Ask which preparation and refinishing approach suits the actual condition and goals of your vehicle."
        ]
      }
    ],
    "checklist": {
      "heading": "Prepare a restoration conversation",
      "items": [
        "Vehicle or equipment identity and the project goal.",
        "Known repair, coating, stripping or blasting history.",
        "Body damage, rust or unfinished areas to review.",
        "Color references, paint codes and desired finish.",
        "Optional photos, coverage details and preferred timing."
      ]
    },
    "faqs": [
      {
        "question": "Is this page the full PPG manual?",
        "answer": "No. It is a short planning overview. The linked PPG resource provides the manufacturer's guide and related technical information."
      },
      {
        "question": "Can I choose a color before the bodywork is assessed?",
        "answer": "You can share the color you have in mind at the start. The repair and preparation scope still needs assessment alongside the finish requirements."
      },
      {
        "question": "Can I inquire about painting without a collision?",
        "answer": "Yes. Explain the painting or restoration work you want, the condition of the surface and the areas to include. Photos are optional."
      },
      {
        "question": "What areas should I include in a restoration quote request?",
        "answer": "List the panels or equipment surfaces you want finished, along with any body damage, rust or unfinished preparation. Explain whether the goal is matching an existing finish or changing the appearance."
      }
    ],
    "note": {
      "heading": "Read PPG's original resource",
      "text": "The Custom Restoration Guide was developed by PPG Refinish. Consult PPG's current product data sheets for product-specific information alongside the guide.",
      "href": "https://www.ppg.com/en-US/refinish/support/user-guides-and-manuals/custom-restoration-guide",
      "linkLabel": "View PPG's Custom Restoration Guide"
    },
    "related": [
      {
        "path": "/Commercial-Truck-Paint-Shop",
        "label": "Painting and refinishing"
      },
      {
        "path": "/Identifying-Common-Paint-Defects",
        "label": "Common paint defects"
      },
      {
        "path": "/earthmoving-equipment-restoration-paint-repair.html",
        "label": "Equipment restoration and painting"
      }
    ],
    "sourceUrls": [
      "https://www.motorcitytruckcollision.com/PPG-Custom-Auto-Restoration-Guide",
      "https://www.ppg.com/en-US/refinish/support/user-guides-and-manuals/custom-restoration-guide",
      "https://www.motorcitytruckcollision.com/Commercial-Truck-Paint-Shop"
    ],
    "preservedFacts": [
      "PPG reference identity, substrate preparation, bodywork, color selection, painting and current product data sheet topics retained.",
      "Original summary replaces22,476words of reproduced source material.",
      "Primary PPG resource attribution/link supplied.",
      "No claim of PPG certification, training completion, product guarantee or specific shop paint system."
    ]
  },
  {
    "id": "rust-prevention-guide",
    "path": "/Rust-Prevention-How-You-Can-Slow-Down-Rust-On-Your-Vehicle",
    "kind": "guide",
    "navLabel": "Rust Prevention Guide",
    "eyebrow": "Rust and body care",
    "title": "Rust Prevention for Working Vehicles | Motor City Truck Collision",
    "description": "Plan rust and corrosion care for trucks and commercial bodies in Michigan. Learn which finish, drainage and body-condition details to discuss with the shop.",
    "h1": "Slowing Rust on Your Vehicle",
    "intro": "Salt, moisture and damaged finishes are familiar concerns for working vehicles in Michigan. Rust prevention starts with routine care and noticing changes early; existing corrosion also needs an assessment of the affected metal and surrounding bodywork.",
    "answer": {
      "question": "What helps slow rust on a truck or commercial body?",
      "text": "Keep salt and dirt from accumulating, care for the finish, keep designed drainage openings clear and address damaged paint or visible corrosion. Follow the vehicle and care-product instructions. If rust has affected a door, compartment, panel or support, discuss the repair before choosing a coating."
    },
    "sections": [
      {
        "heading": "Keep track of exposed and damaged areas",
        "paragraphs": [
          "Paint chips, scratches, door bottoms, cab corners, wheel openings and cargo-body surfaces are useful places to note changes. Tell the shop whether corrosion appears limited to the surface or includes flaking metal, holes or changes in panel fit. Photos can start the discussion."
        ]
      },
      {
        "heading": "Make cleaning and drainage part of routine care",
        "paragraphs": [
          "Remove road salt and debris, care for painted surfaces and keep designed drainage openings clear. Use cleaning and protection methods appropriate to the vehicle and finish. Ask the shop about care for freshly refinished areas."
        ]
      },
      {
        "heading": "Discuss rust repair and refinishing together",
        "paragraphs": [
          "A coating does not answer every rust concern. The condition of the metal, joints and nearby body components affects what work is needed. Explain whether the goal is finish care, body repair or refurbishment, and include any previous rust treatment you know about."
        ]
      }
    ],
    "checklist": {
      "heading": "Details for a rust inquiry",
      "items": [
        "Vehicle type and the exact rust locations.",
        "Changes in doors, compartments, panel edges or body fit.",
        "Known earlier repairs, undercoating or rust treatments.",
        "Optional photos of the affected area and surrounding body.",
        "Whether you want repair, refinishing or care advice."
      ]
    },
    "faqs": [
      {
        "question": "Will repainting remove a rust problem?",
        "answer": "The metal and coating condition need review first. Discuss the corrosion with the shop so the proposed body and finish work addresses the area being assessed."
      },
      {
        "question": "Can I ask about rust on a service body?",
        "answer": "Yes. Include doors, hinge areas, compartments and nearby panels in your description. Those details help distinguish a finish concern from body repair needs."
      },
      {
        "question": "How often should I inspect for rust?",
        "answer": "Use the vehicle's care guidance and account for salt exposure and working conditions. A fixed schedule on a general webpage cannot describe every vehicle or existing finish."
      }
    ],
    "related": [
      {
        "path": "/Service-Body-Repair",
        "label": "Service body repair"
      },
      {
        "path": "/Commercial-Truck-Paint-Shop",
        "label": "Body refinishing and painting"
      },
      {
        "path": "/dustless-sandblasting.html",
        "label": "Sandblasting inquiries"
      }
    ],
    "sourceUrls": [
      "https://www.motorcitytruckcollision.com/Rust-Prevention-How-You-Can-Slow-Down-Rust-On-Your-Vehicle",
      "https://www.motorcitytruckcollision.com/Service-Body-Repair",
      "https://www.motorcitytruckcollision.com/Commercial-Truck-Paint-Shop"
    ],
    "preservedFacts": [
      "Michigan salt/moisture, cleaning, finish protection, drainage, paint chips and routine rust observation topics retained.",
      "Body-area examples retained without remote structural or roadworthiness judgments.",
      "Rust/body/refinish assessment replaces blanket rustproofing or permanent-prevention promises.",
      "Unrelated RV marketing and unsupported superlatives omitted."
    ]
  },
  {
    "id": "regular-trailer-maintenance-guide",
    "path": "/Top-Trailer-Maintenance-Services-That-Must-Be-Done-Regularly",
    "kind": "guide",
    "navLabel": "Trailer Maintenance Guide",
    "eyebrow": "Trailer maintenance planning",
    "title": "Regular Trailer Maintenance Guide | Motor City Truck Collision",
    "description": "Organize trailer maintenance around component manuals, service records and body condition. Separate exterior repairs from brakes, bearings and other mechanical work.",
    "h1": "Regular Trailer Maintenance: What to Plan",
    "intro": "A useful trailer maintenance plan covers the trailer's running gear, electrical equipment and body condition. This guide helps owners organize service questions and records, with exterior body repairs directed to Motor City Truck Collision and mechanical needs to the appropriate service provider.",
    "answer": {
      "question": "Which areas belong in a regular trailer maintenance plan?",
      "text": "Include bearings, brakes, tires, suspension, coupling equipment, lighting and any fitted breakaway system, plus the frame, deck, roof, walls, doors and ramps. Use the trailer and component manufacturers' service instructions for intervals and procedures rather than applying one schedule to every trailer."
    },
    "sections": [
      {
        "heading": "Keep the mechanical service records together",
        "paragraphs": [
          "Record earlier bearing, brake, axle, suspension, tire and wiring work. Share the trailer's identity and how it is used with the mechanical service provider. Mileage, heavy use and storage history can be relevant to the discussion, along with any new operating symptoms."
        ]
      },
      {
        "heading": "Include the parts that carry and protect the load",
        "paragraphs": [
          "Flooring, decks, roofs, sidewalls, doors, ramps and latches also deserve attention in the service plan. Describe damaged panels, water entry, corrosion or worn loading areas. A body repair inquiry should explain the affected components and how the trailer is used."
        ]
      },
      {
        "heading": "Choose the right service contact",
        "paragraphs": [
          "Motor City Truck Collision focuses on exterior body and paint work. Brakes, bearings, axles, suspension and other mechanical needs require the appropriate trailer service contact. Explain the complete issue when arranging service and confirm which provider will handle each part."
        ]
      }
    ],
    "checklist": {
      "heading": "Build a useful maintenance request",
      "items": [
        "Trailer make, model, type and component information if available.",
        "Service records and manufacturer instructions.",
        "Current mechanical symptoms to discuss with the service provider.",
        "Body, roof, deck, door or ramp damage to assess.",
        "Your preferred service window and working schedule."
      ]
    },
    "faqs": [
      {
        "question": "Where should I find the right maintenance intervals?",
        "answer": "Start with the trailer and component manufacturers' guidance and existing service records. Ask the mechanical service provider how the trailer's use affects the plan."
      },
      {
        "question": "Does checking the exterior replace mechanical service?",
        "answer": "No. Body condition and mechanical maintenance are separate parts of the plan. Arrange the appropriate assessment for the systems involved."
      },
      {
        "question": "Where should I ask about bearings or brakes?",
        "answer": "Contact Motor City Trailer Repair and confirm the required service. Include the trailer details and prior work when discussing the job."
      }
    ],
    "note": {
      "heading": "Bodywork or mechanical service?",
      "text": "Motor City Truck Collision handles exterior body and paint work. For trailer brakes, bearings, axles, suspension or other mechanical needs, contact Motor City Trailer Repair and confirm the service you need.",
      "href": "https://www.motorcitytrailerrepair.com/",
      "linkLabel": "Visit Motor City Trailer Repair"
    },
    "related": [
      {
        "path": "/Trailer-Repair",
        "label": "Trailer body repair"
      },
      {
        "path": "/Landscaping-Trailer-Repair-and-Maintenance",
        "label": "Landscaping trailer planning"
      },
      {
        "path": "/Complete-RV-Trailer-Repair-Maintenance",
        "label": "RV trailer systems guide"
      }
    ],
    "sourceUrls": [
      "https://www.motorcitytruckcollision.com/Top-Trailer-Maintenance-Services-That-Must-Be-Done-Regularly",
      "https://www.motorcitytruckcollision.com/Trailer-Repair",
      "https://www.motorcitytrailerrepair.com/"
    ],
    "preservedFacts": [
      "Bearings, brakes, tires, suspension, lights, coupling, breakaway, body, floor, roof, door and ramp topics retained.",
      "Fixed mileage/year and tire-age instructions replaced with manufacturer-specific planning.",
      "Mechanical routing separated from Motor City Truck Collision's body/paint scope.",
      "No inspection certification, DOT guarantee, maintenance-package or roadworthiness promise."
    ]
  },
  {
    "id": "rv-travel-trailer-guide-michigan",
    "path": "/Popular-RV-Travel-Trailers-in-Michigan",
    "kind": "guide",
    "navLabel": "Michigan RV Trailer Guide",
    "eyebrow": "RV trailer guide",
    "title": "RV Travel Trailers in Michigan: A Guide | Motor City Truck Collision",
    "description": "Explore travel-trailer and fifth-wheel examples and the details useful for repair planning: model year, body type, roof, walls, compartments and service needs.",
    "h1": "RV Travel Trailers in Michigan",
    "intro": "Travel trailers and fifth wheels come in many sizes and layouts. Knowing the model and body type helps when discussing exterior damage or service needs. This guide introduces several trailer families and the details useful for repair planning.",
    "answer": {
      "question": "Does an RV's brand name provide enough information for a repair inquiry?",
      "text": "A brand is a starting point. Add the model, year, trailer type and affected body or roof area. Different floorplans and model years can have different components, so details about your actual trailer matter more than a general reputation or popularity claim."
    },
    "sections": [
      {
        "heading": "Travel-trailer examples",
        "paragraphs": [
          "Examples include Forest River Cherokee, Salem and Wildwood; Jayco Jay Flight; Keystone Hideout and Springdale; Grand Design Imagine; Heartland North Trail and Wilderness; and Coachmen Catalina and Apex. These names help identify trailer families. Check the manufacturer's information for the specific year and model rather than assuming all versions share the same features."
        ]
      },
      {
        "heading": "Fifth wheels are a different trailer category",
        "paragraphs": [
          "Alliance Paradigm and Keystone Montana are fifth-wheel examples discussed in this guide. For repair planning, describe the actual roof, sidewall, caps, compartments or slide-out body area involved. A model-family name does not establish the material, condition or repair scope of the individual unit."
        ]
      },
      {
        "heading": "Match the inquiry to the problem",
        "paragraphs": [
          "Exterior collision damage, roof concerns, fiberglass cracks and paintwork belong in a body repair discussion. Running-gear or interior-system problems need the appropriate service contact. Explain where the problem appears and how it affects use; confirm provider scope before arranging an appointment."
        ]
      }
    ],
    "checklist": {
      "heading": "Identify your RV before asking about repairs",
      "items": [
        "Brand, model, year and exact model or floorplan designation if known.",
        "Travel trailer, fifth wheel or another towable type.",
        "Affected roof, wall, cap, compartment or exterior panel.",
        "Known damage, water-entry history and earlier repairs.",
        "Optional photos and the timing you would like to discuss."
      ]
    },
    "faqs": [
      {
        "question": "How should I use a trailer model-family name when asking about repairs?",
        "answer": "Include it with the model year and specific trailer details. A family name helps identify the unit, but the roof, body material, condition and damage need their own review."
      },
      {
        "question": "Do current model specifications describe an older trailer?",
        "answer": "Not necessarily. Use documentation for your actual model year and share any known modifications or previous repairs."
      },
      {
        "question": "Can I ask about a brand that is not listed?",
        "answer": "Yes. Provide the trailer details and work needed so the appropriate shop can discuss whether it can assess the job."
      }
    ],
    "related": [
      {
        "path": "/rv-body-roof-repair-guide",
        "label": "RV body and roof guide"
      },
      {
        "path": "/RV-Repair",
        "label": "RV exterior repairs"
      },
      {
        "path": "/Complete-RV-Trailer-Repair-Maintenance",
        "label": "RV repair and service planning"
      }
    ],
    "sourceUrls": [
      "https://www.motorcitytruckcollision.com/Popular-RV-Travel-Trailers-in-Michigan",
      "https://www.motorcitytruckcollision.com/RV-Repair",
      "https://www.motorcitytruckcollision.com/Complete-RV-Trailer-Repair-Maintenance"
    ],
    "preservedFacts": [
      "Existing URL and RV trailer-family topic retained.",
      "Existing model-family examples retained:Alliance Paradigm, Keystone Montana, Forest River Cherokee/Salem/Wildwood, Jayco JayFlight, Keystone Hideout/Springdale, GrandDesign Imagine, Heartland NorthTrail/Wilderness, Coachmen Catalina/Apex.",
      "Unverified popularity, best-selling, luxury superiority, current feature specifications, dealer availability and purchase recommendations removed.",
      "No promise of interior/mechanical services at Motor City Truck Collision."
    ]
  },
  {
    "id": "sandblasting-cost-guide",
    "path": "/how-much-does-it-cost-to-sandblast.html",
    "kind": "guide",
    "navLabel": "Sandblasting Cost Guide",
    "eyebrow": "Sandblasting estimates",
    "title": "What Affects Sandblasting Cost? | Motor City Truck Collision",
    "description": "Understand sandblasting estimate factors including size, coating, material, access, setup and the next finish. Learn which project details help the Brighton shop.",
    "h1": "How Much Does Sandblasting Cost?",
    "intro": "Sandblasting cost depends on the item, its condition and the preparation work required. A useful inquiry describes the surface and the result you need, rather than treating every truck, part, trailer or machine as the same job.",
    "answer": {
      "question": "What determines the cost of a sandblasting job?",
      "text": "The main factors include the item size and material, the coating or corrosion to remove, access to the surfaces, the appropriate blasting approach, and setup and handling. The intended finish and where the work can take place also affect the scope."
    },
    "sections": [
      {
        "heading": "Describe the surface and the goal",
        "paragraphs": [
          "Identify the item and the material if known. Explain whether it has old paint, corrosion or another coating, and whether the aim is cleaning, preparation for paint or part of a larger refurbishment. Photos and approximate dimensions can help explain the work."
        ]
      },
      {
        "heading": "Small parts still need preparation and setup",
        "paragraphs": [
          "The existing cost guide explains that setting up, handling and finishing a job takes time even when the surface is small. Price cannot be judged from the time spent blasting alone. Ask how the proposed scope is defined instead of assuming a universal price per item."
        ]
      },
      {
        "heading": "Discuss location and the next step",
        "paragraphs": [
          "Tell the shop whether the item is a loose part, a vehicle, trailer or fixed piece of equipment. If location or transport is a concern, ask which arrangement is available. Also explain whether the project needs painting afterward so preparation and finish requirements can be discussed together."
        ]
      }
    ],
    "checklist": {
      "heading": "Details for a sandblasting estimate",
      "items": [
        "Item type, approximate size and material if known.",
        "Old coating, corrosion and the areas to be prepared.",
        "The result you need and any planned next finish.",
        "Access or handling concerns to discuss with the shop.",
        "Optional photos and your preferred project timing."
      ]
    },
    "faqs": [
      {
        "question": "Is there one price for sandblasting a truck or trailer?",
        "answer": "No general figure describes every item and condition. Share the surface area, coating and project goal so the shop can discuss the actual scope."
      },
      {
        "question": "Why can a small part have a noticeable setup cost?",
        "answer": "Handling and equipment preparation are still part of the job. The amount of surface being blasted is only one cost factor."
      },
      {
        "question": "Can combining several items reduce cost?",
        "answer": "Ask the shop whether grouping the work is appropriate for the project and schedule. The quote depends on the items and work being reviewed."
      },
      {
        "question": "What if the equipment cannot come to the shop?",
        "answer": "Describe the item, its location and the areas needing preparation. Ask which arrangements are available before planning site work or transport."
      }
    ],
    "related": [
      {
        "path": "/dustless-sandblasting.html",
        "label": "Sandblasting services"
      },
      {
        "path": "/sandblasting-industrial-machinery.html",
        "label": "Industrial machinery preparation"
      },
      {
        "path": "/earthmoving-equipment-restoration-paint-repair.html",
        "label": "Equipment restoration and painting"
      }
    ],
    "sourceUrls": [
      "https://www.motorcitytruckcollision.com/how-much-does-it-cost-to-sandblast.html",
      "https://www.motorcitytruckcollision.com/dustless-sandblasting.html",
      "https://www.motorcitytruckcollision.com/sandblasting-industrial-machinery.html"
    ],
    "preservedFacts": [
      "Material, abrasive/method choice, item/location, setup/handling and scheduling factors retained.",
      "No historic$100example, minute-level setup times, universal rate, mobile-service promise or guaranteed batching discount reused.",
      "Sandblasting and preparation intent remains distinct from the broad service page."
    ]
  },
  {
    "id": "complete-rv-trailer-repair-maintenance-guide",
    "path": "/Complete-RV-Trailer-Repair-Maintenance",
    "kind": "guide",
    "navLabel": "RV Trailer Systems Guide",
    "eyebrow": "RV service planning",
    "title": "RV Trailer Repair and Maintenance Planning | Motor City Truck Collision",
    "description": "Sort RV trailer concerns into exterior bodywork, running gear and interior systems. Prepare a clearer service inquiry and confirm the right provider for each job.",
    "h1": "RV Trailer Repair & Maintenance Planning",
    "intro": "A towable RV combines an exterior body, a trailer chassis and living systems. Sorting an issue into those areas helps you reach the right service contact. Motor City Truck Collision's role here is exterior collision, body, roof and paint work.",
    "answer": {
      "question": "How should I organize an RV trailer repair inquiry?",
      "text": "Separate exterior body and water-entry concerns from running-gear problems and interior-system faults. List the affected parts, when the problem started and any known damage. Confirm which provider handles each system instead of assuming one body repair appointment covers every RV function."
    },
    "sections": [
      {
        "heading": "Exterior body and roof concerns",
        "paragraphs": [
          "Roof openings, damaged seams, sidewalls, front or rear caps, corners, compartments and exterior trim belong in a body repair discussion. Mention cracks, impact damage, water staining or changed panel fit. Photos can help describe the concern, and paint or exterior finish needs can be included."
        ]
      },
      {
        "heading": "Mechanical and running-gear needs",
        "paragraphs": [
          "Axles, suspension, brakes, bearings, coupling equipment and trailer wiring require the appropriate mechanical service. Keep component information and service history with your inquiry. Contact Motor City Trailer Repair to discuss the need and confirm service availability before arranging work."
        ]
      },
      {
        "heading": "Interior systems need their own service check",
        "paragraphs": [
          "Plumbing, appliances, propane equipment, heating, cooling and other living systems are different from exterior bodywork. Explain which system is affected to a qualified RV service provider. For a slide-out concern, describe whether the issue is the exterior wall, seal area or operating mechanism so scope can be clarified."
        ]
      }
    ],
    "checklist": {
      "heading": "Prepare a system-by-system request",
      "items": [
        "RV make, model, year and towable type.",
        "A separate list of exterior, mechanical and interior concerns.",
        "When each issue appeared and any known impact or water entry.",
        "Service history and component information if available.",
        "Optional photos and your preferred repair window."
      ]
    },
    "faqs": [
      {
        "question": "Which RV trailer work does Motor City Truck Collision handle?",
        "answer": "Motor City Truck Collision handles exterior body, roof, collision and paint work. Mechanical and interior-system concerns need the appropriate service provider."
      },
      {
        "question": "Who should I contact about trailer brakes or bearings?",
        "answer": "Contact Motor City Trailer Repair and confirm the service required. Include the RV and component details and earlier maintenance records."
      },
      {
        "question": "Can water damage involve more than the exterior?",
        "answer": "Report any visible interior staining or damage along with the exterior concern. Confirm the inspection and repair scope with the appropriate provider rather than assuming all related work is included."
      }
    ],
    "note": {
      "heading": "Bodywork or mechanical service?",
      "text": "Motor City Truck Collision handles exterior body and paint work. For trailer brakes, bearings, axles, suspension or other mechanical needs, contact Motor City Trailer Repair and confirm the service you need.",
      "href": "https://www.motorcitytrailerrepair.com/",
      "linkLabel": "Visit Motor City Trailer Repair"
    },
    "related": [
      {
        "path": "/RV-Repair",
        "label": "RV exterior repair"
      },
      {
        "path": "/rv-body-roof-repair-guide",
        "label": "RV body and roof guide"
      },
      {
        "path": "/Top-Trailer-Maintenance-Services-That-Must-Be-Done-Regularly",
        "label": "Trailer maintenance planning"
      }
    ],
    "sourceUrls": [
      "https://www.motorcitytruckcollision.com/Complete-RV-Trailer-Repair-Maintenance",
      "https://www.motorcitytruckcollision.com/RV-Repair",
      "https://www.motorcitytrailerrepair.com/"
    ],
    "preservedFacts": [
      "Existing distinction between Body&Exterior, Mechanical&Structural, andInteriorHomeSystems retained as a service-routing guide.",
      "Roof, sidewall, caps, water intrusion, compartments, trim, slide-out body and paint topics retained.",
      "Mechanical and interior service categories preserved for clarity without promising them at the collision shop.",
      "Removed blanketcertification, top-to-bottomservice, safety/availability and turnaround claims."
    ]
  },
  {
    "id": "landscaping-trailer-repair-maintenance-guide",
    "path": "/Landscaping-Trailer-Repair-and-Maintenance",
    "kind": "guide",
    "navLabel": "Landscaping Trailer Guide",
    "eyebrow": "Contractor trailer planning",
    "title": "Landscaping Trailer Repair Planning | Motor City Truck Collision",
    "description": "Plan landscaping trailer body, deck, ramp, gate and rust repairs around daily use. Separate bodywork from mechanical maintenance and prepare useful project details.",
    "h1": "Landscaping Trailer Repair & Maintenance",
    "intro": "Loading equipment, hauling material and working through Michigan weather can leave a landscaping trailer with several concerns at once. This guide helps contractors separate body and deck damage from mechanical maintenance and organize a useful repair inquiry.",
    "answer": {
      "question": "What should a landscaping trailer repair request include?",
      "text": "Describe the trailer type, how it is used and the affected deck, ramps, gates, latches, frame or body panels. List mechanical symptoms separately. Include previous repairs, rust concerns and the work window you would like to discuss so the appropriate provider can review the job."
    },
    "sections": [
      {
        "heading": "Explain the loading areas and body damage",
        "paragraphs": [
          "Floorboards, decks, ramps, tailgates and latches see repeated use. Mention loose or damaged areas, changes in operation and any impact. For enclosed trailers, include doors, roof and sidewalls. A clear description helps the body repair discussion cover connected damage rather than one panel alone."
        ]
      },
      {
        "heading": "Include corrosion and finish requirements",
        "paragraphs": [
          "Consider rust removal, sandblasting and repainting alongside trailer bodywork. Explain where corrosion appears and whether you want repairs, refinishing or a larger refurbishment. Include company colors or lettering requirements if relevant, without assuming every project needs a complete repaint."
        ]
      },
      {
        "heading": "Plan the mechanical work separately",
        "paragraphs": [
          "Brakes, bearings, axles, suspension, coupling equipment and wiring belong with the appropriate mechanical service contact. Share the trailer's records and symptoms, and confirm availability. If body and mechanical concerns occur together, ask how the inquiries should be coordinated; do not assume a fixed turnaround or package."
        ]
      }
    ],
    "checklist": {
      "heading": "Details for a contractor trailer inquiry",
      "items": [
        "Trailer type, size and typical equipment or material carried.",
        "Deck, ramp, gate, door or panel damage to review.",
        "Corrosion locations and known earlier repairs.",
        "Mechanical concerns listed separately for the service provider.",
        "Optional photos, finish requirements and preferred timing."
      ]
    },
    "faqs": [
      {
        "question": "Can I ask about a damaged ramp or trailer floor?",
        "answer": "Yes. Describe the affected area, how it is used and any surrounding body or frame damage so the shop can discuss the assessment."
      },
      {
        "question": "Does visible rust mean the frame must be replaced?",
        "answer": "Appearance alone does not establish the repair scope. The affected metal and surrounding components need assessment before a repair or replacement plan is discussed."
      },
      {
        "question": "How can I plan repairs around a busy landscaping season?",
        "answer": "Share your working schedule, the damage and preferred service window. The provider can discuss the work and available timing after reviewing the inquiry."
      },
      {
        "question": "Who handles mechanical trailer service?",
        "answer": "Contact Motor City Trailer Repair and confirm the required brake, bearing, axle, suspension or other mechanical work."
      }
    ],
    "note": {
      "heading": "Bodywork or mechanical service?",
      "text": "Motor City Truck Collision handles exterior body and paint work. For trailer brakes, bearings, axles, suspension or other mechanical needs, contact Motor City Trailer Repair and confirm the service you need.",
      "href": "https://www.motorcitytrailerrepair.com/",
      "linkLabel": "Visit Motor City Trailer Repair"
    },
    "related": [
      {
        "path": "/Trailer-Repair",
        "label": "Trailer body repair"
      },
      {
        "path": "/Enclosed-Trailer-Repair",
        "label": "Enclosed trailer bodywork"
      },
      {
        "path": "/Top-Trailer-Maintenance-Services-That-Must-Be-Done-Regularly",
        "label": "Regular maintenance planning"
      }
    ],
    "sourceUrls": [
      "https://www.motorcitytruckcollision.com/Landscaping-Trailer-Repair-and-Maintenance",
      "https://www.motorcitytruckcollision.com/Trailer-Repair",
      "https://www.motorcitytruckcollision.com/Enclosed-Trailer-Repair",
      "https://www.motorcitytrailerrepair.com/"
    ],
    "preservedFacts": [
      "Landscaper/contractor audience, deck, ramp, gate, latch, floor, frame, body, rust, sandblasting and paint topics retained.",
      "Seasonal working-use context retained without exactseasonalpackage, fleetplan or rapidturnaround promise.",
      "Mechanical service topics routed separately; no blanketcertification, DOTcomplianceorremote towing/drivingclearance."
    ]
  },
  {
    "id": "enclosed-trailer-wall-bowing-guide",
    "path": "/why-enclosed-trailers-get-wall-bowing",
    "kind": "guide",
    "navLabel": "Enclosed Trailer Wall Bowing",
    "eyebrow": "Panels, framing and body openings",
    "title": "Why Enclosed Trailer Walls Bow | Motor City Truck Collision",
    "description": "Understand visible enclosed-trailer wall bowing, gaps and door changes. Organize the damage details for a professional body assessment in Brighton.",
    "h1": "Why Enclosed Trailer Walls Can Bow",
    "intro": "An enclosed trailer wall can bow when its panels or supporting assembly are damaged or lose stiffness. Motor City Truck Collision in Brighton assesses enclosed-trailer body damage. This guide helps you record visible changes and explain the concern before a professional assessment.",
    "answer": {
      "question": "What can cause wall bowing in an enclosed trailer?",
      "text": "An enclosed trailer’s wall assembly can be affected by concentrated cargo pressure, an impact, damaged bracing, loose or corroded fasteners, or moisture damage between its layers. A bulge alone does not identify the cause. The panel, interior support and adjoining roof or floor connections need to be considered together."
    },
    "sections": [
      {
        "heading": "Record what changed",
        "paragraphs": [
          "Compare the affected wall with the other side where possible. Note bulging panels, gaps at seams, visible fastener changes or doors that no longer line up as before. Explain when the change first appeared and whether an impact or loading event preceded it."
        ]
      },
      {
        "heading": "Include the inside and the adjoining body",
        "paragraphs": [
          "The exterior skin is only part of the wall. Interior lining, bracing and the connections at the roof, header and sill may be relevant to the assessment.",
          "Describe water entry or corrosion around the affected area. Include visible damage to nearby doors or corners, rather than limiting the request to a close-up of the bulge."
        ]
      },
      {
        "heading": "Define the assessment before the repair",
        "paragraphs": [
          "Panel repair, replacement material and work to the supporting assembly are different scopes. The shop needs to review the construction and connected damage before proposing a repair.",
          "Include the trailer's cargo use, available manufacturer details and previous body repairs. Those details help explain the condition; they do not establish the trailer's structural capacity."
        ]
      }
    ],
    "checklist": {
      "heading": "Information for a wall-bowing assessment",
      "items": [
        "Trailer manufacturer, size and body type",
        "Wide views of the affected wall and opposite side",
        "Visible seams, fasteners and adjoining openings",
        "Interior lining or bracing damage, if visible",
        "When the bowing appeared and any related impact or leak"
      ]
    },
    "faqs": [
      {
        "question": "Does a bowed outer panel prove that the frame is bent?",
        "answer": "No. The visible panel shape does not establish the condition of the whole assembly. The assessment needs to consider the wall supports and adjoining structure."
      },
      {
        "question": "Why mention doors that no longer close evenly?",
        "answer": "A change at a door opening can help describe connected body damage. Include the door and surrounding wall in the request."
      },
      {
        "question": "Can photos replace an in-person structural assessment?",
        "answer": "Photos help document visible conditions. They do not establish hidden damage or load-carrying capacity; confirm the inspection needed with the appropriate shop."
      }
    ],
    "related": [
      {
        "path": "/Enclosed-Trailer-Repair",
        "label": "Enclosed trailer body repair"
      },
      {
        "path": "/Trailer-Repair",
        "label": "Trailer collision bodywork"
      },
      {
        "path": "/Landscaping-Trailer-Repair-and-Maintenance",
        "label": "Trailer repair planning"
      }
    ],
    "sourceUrls": [
      "https://www.motorcitytruckcollision.com/why-enclosed-trailers-get-wall-bowing",
      "https://www.motorcitytruckcollision.com/Enclosed-Trailer-Repair"
    ],
    "preservedFacts": [
      "Cargo pressure/impact, bracing, fasteners and moisture can affect wall assemblies.",
      "Visible bulges, gaps and door-alignment changes are reportable conditions.",
      "Inspect panel and supporting assembly together."
    ],
    "existingAliases": [
      "/308.html"
    ]
  },
  {
    "id": "box-truck-roof-leak-causes-guide",
    "path": "/common-causes-of-box-truck-roof-leaks-and-how-to-fix-them",
    "kind": "guide",
    "navLabel": "Box Truck Roof Leak Causes",
    "eyebrow": "Roof joints and water intrusion",
    "title": "Box Truck Roof Leak Causes and Repair Assessment | Motor City Truck Collision",
    "description": "Learn which box truck roof joints and damage details matter in a leak assessment. Motor City Truck Collision in Brighton reviews roof and related body damage.",
    "h1": "Common Causes of Box Truck Roof Leaks",
    "intro": "A box truck roof leak can begin at a damaged panel, roof seam, fastener opening or fitted roof accessory. Water may travel before appearing inside the cargo box. Use these observations to prepare a roof and body assessment with Motor City Truck Collision in Brighton.",
    "answer": {
      "question": "Where can a box truck roof leak start?",
      "text": "Possible entry points include damaged roof skin, open seams, fastener holes, roof-to-wall joints and openings around fitted vents or accessories. An impact near a rear header can also affect the adjoining seals. Interior stains identify where water appears, but may not identify where it enters."
    },
    "sections": [
      {
        "heading": "Record the leak pattern",
        "paragraphs": [
          "Note when water appears, where the cargo box becomes wet and whether the leak followed an impact or roof alteration. Include ceiling or lining stains, wet insulation where visible and damage to walls or flooring."
        ]
      },
      {
        "heading": "Show the roof and adjoining joints",
        "paragraphs": [
          "A wide view helps locate the damaged area on the box. Close-ups can show torn skin, gaps, seams or accessory mounts. Also include the front wall, rear header or corner when the opening is near an edge.",
          "Describe the roof material if known and any previous repairs. Different roof construction and damage require different repair materials and preparation."
        ]
      },
      {
        "heading": "Choose repair scope after the opening is assessed",
        "paragraphs": [
          "A small joint repair differs from replacing a damaged panel or addressing water-affected interior material. Sealant alone does not replace torn or missing roof skin.",
          "The assessment should identify the opening and the connected damage before the repair is priced. Discuss any lining or floor work separately within the overall body-repair request."
        ]
      }
    ],
    "checklist": {
      "heading": "Useful roof-leak details",
      "items": [
        "Truck and cargo-body identification",
        "When the leak appears and any known impact",
        "Wide roof view and close-ups of visible openings",
        "Interior stains or water-affected material",
        "Previous roof repairs or fitted roof accessories"
      ]
    },
    "faqs": [
      {
        "question": "Why can a stain appear away from the opening?",
        "answer": "Water can travel along the roof or adjoining materials before becoming visible. Include both the interior stain and suspected exterior entry areas."
      },
      {
        "question": "Does every leak need a replacement roof panel?",
        "answer": "The opening, roof material and surrounding condition determine the assessment. A local joint problem and a torn panel are different scopes."
      },
      {
        "question": "Should I mention damaged flooring beneath the leak?",
        "answer": "Yes. Include the floor and lining condition so the request addresses the water-affected body areas as well as the entry point."
      }
    ],
    "related": [
      {
        "path": "/box-truck-roof-leak-body-repair",
        "label": "Roof leak and body repair"
      },
      {
        "path": "/Box-Truck-Roof-Repair-Cost",
        "label": "Roof repair quote factors"
      },
      {
        "path": "/box-truck-repair.html",
        "label": "Box truck bodywork"
      }
    ],
    "sourceUrls": [
      "https://www.motorcitytruckcollision.com/common-causes-of-box-truck-roof-leaks-and-how-to-fix-them",
      "https://www.motorcitytruckcollision.com/Box-Truck-Roof-Repair-Cost"
    ],
    "preservedFacts": [
      "Seams, fasteners, roof skin, accessory openings and adjoining headers can be relevant.",
      "Water may travel away from its entry point.",
      "Interior lining/insulation/floor damage should be reported."
    ],
    "existingAliases": [
      "/211.html"
    ]
  },
  {
    "id": "commercial-truck-structural-signs-guide",
    "path": "/how-to-spot-early-signs-of-structural-damage-on-commercial-trucks",
    "kind": "guide",
    "navLabel": "Structural Damage: Visible Signs",
    "eyebrow": "Frame, mounts and body changes",
    "title": "Visible Signs of Truck Structural Damage | Motor City Truck Collision",
    "description": "Record panel gaps, damaged mounts, floor changes and other visible concerns after an impact. Arrange professional truck body and frame assessment in Brighton.",
    "h1": "Signs of Structural Damage on a Commercial Truck",
    "intro": "A commercial truck can show changes around its body, mounting points or floor after an impact. Motor City Truck Collision in Brighton assesses commercial body and related frame damage. These observations help explain a concern; they do not diagnose the structure from appearance alone.",
    "answer": {
      "question": "Which visible changes should I report after a truck impact?",
      "text": "Report new panel gaps, doors that no longer align, visible bends or cracks, damaged brackets, missing fasteners, changes around body mounts and affected floor areas. Include when the changes appeared and which part of the truck was hit. A professional assessment establishes how those observations relate to the structure."
    },
    "sections": [
      {
        "heading": "Compare the condition before and after the impact",
        "paragraphs": [
          "Identify what is new, what was present beforehand and any earlier body repairs. A door or panel that changed position can be useful information even when the outer dent looks minor.",
          "If there are operating symptoms such as new vibration or steering changes, describe them separately. They may require an appropriate mechanical assessment as well as bodywork."
        ]
      },
      {
        "heading": "Include mounts and the affected cargo area",
        "paragraphs": [
          "Body panels, frame rails, cross-members and mounting areas are connected parts of the vehicle, but they are not interchangeable. Show the area around the impact and any visible damage near the body supports.",
          "Describe floor movement, separation or new gaps without assuming which component caused them. Photos of accessible visible areas can begin the discussion."
        ]
      },
      {
        "heading": "Keep the inspection scope clear",
        "paragraphs": [
          "The assessment may need to review the supporting structure as well as the damaged skin. Fleet unit details, previous work and the condition on the opposite side help provide context.",
          "Ask the shop which inspection and measurement work is needed. Visible photos alone do not establish hidden damage or the vehicle's operating condition."
        ]
      }
    ],
    "checklist": {
      "heading": "Prepare a structural-concern request",
      "items": [
        "Vehicle, body type and fleet unit number",
        "Known impact location and timing",
        "New gaps, door changes or visible damaged mounts",
        "Affected floor areas and previous repairs",
        "Separate description of any operating symptoms"
      ]
    },
    "faqs": [
      {
        "question": "Does a misaligned door prove that the frame is bent?",
        "answer": "No. It is a condition to report. The body opening, supports and related structure need assessment before the cause is established."
      },
      {
        "question": "Should I include the opposite side of the truck?",
        "answer": "Yes. A wider view and the opposite side provide context for visible differences around the damaged body or mounting area."
      },
      {
        "question": "Can body and mechanical concerns be reviewed as the same issue?",
        "answer": "Describe both, but confirm the appropriate provider and scope for each. A body assessment does not automatically cover every mechanical symptom."
      }
    ],
    "related": [
      {
        "path": "/Fleet-Truck-Body-Repair",
        "label": "Fleet body and frame assessment"
      },
      {
        "path": "/Semi-Truck-Body-Shop",
        "label": "Semi tractor bodywork"
      },
      {
        "path": "/box-truck-repair.html",
        "label": "Cargo-body repair"
      }
    ],
    "sourceUrls": [
      "https://www.motorcitytruckcollision.com/how-to-spot-early-signs-of-structural-damage-on-commercial-trucks",
      "https://www.motorcitytruckcollision.com/Fleet-Truck-Body-Repair"
    ],
    "preservedFacts": [
      "Gaps, door fit, visible brackets/mounts, cracks and floor changes are reportable signs.",
      "Frame, cross-members, body supports and skin are distinct connected parts.",
      "Mechanical symptoms need separate scope/provider clarification."
    ],
    "existingAliases": [
      "/224.html"
    ]
  },
  {
    "id": "truck-cross-member-damage-guide",
    "path": "/how-cross-members-get-damaged-in-truck-collisions",
    "kind": "guide",
    "navLabel": "Truck Cross-Member Damage",
    "eyebrow": "Frame connections and body supports",
    "title": "Truck Cross-Member Damage After Collisions | Motor City Truck Collision",
    "description": "Understand cross-member damage and the body changes to report after an impact. Request commercial frame and body assessment at Motor City Truck Collision.",
    "h1": "How Truck Cross-Members Can Be Damaged in a Collision",
    "intro": "Cross-members run across the frame between its main rails and help support connected vehicle components. Impacts can damage these members and their attachments. Motor City Truck Collision in Brighton considers related frame damage alongside commercial body repairs.",
    "answer": {
      "question": "How can a collision affect truck cross-members?",
      "text": "An impact to the rear, side or understructure can bend or distort a cross-member or damage its connections. Docks, curbs and other fixed objects can affect the rear structure as well as visible body panels. The assessment should consider the member, attachments and adjoining body or floor supports."
    },
    "sections": [
      {
        "heading": "Identify the impact and connected changes",
        "paragraphs": [
          "Describe where the truck was hit and what changed afterward. Visible bends, cracked areas, damaged brackets or fastening points, floor changes and panels or doors that no longer align are useful observations.",
          "Those conditions do not establish the full extent of frame damage. Include them so the inspection is not limited to the outer skin."
        ]
      },
      {
        "heading": "Include corrosion and previous work",
        "paragraphs": [
          "Corrosion around joints or attachment points can affect the condition being assessed. Note visible rust-through and any known previous frame or body repairs.",
          "Show accessible areas around the damage and a wider view of the body. Do not assume that repairing one visible member addresses every connected component."
        ]
      },
      {
        "heading": "Assess frame and bodywork together",
        "paragraphs": [
          "A cross-member concern may affect the cargo floor, body mounts or rear opening. The repair discussion should identify those connected areas before panel and paint work is finalized.",
          "Provide fleet unit details and desired timing, then confirm what inspection and repair scope the shop can establish. Photographs help communicate the issue; measurements or closer inspection may be needed."
        ]
      }
    ],
    "checklist": {
      "heading": "Details for a cross-member assessment",
      "items": [
        "Truck and cargo-body type",
        "Where and when the impact occurred",
        "Wide views and accessible close-ups of visible damage",
        "Changes around floors, mounts, panels or doors",
        "Corrosion or previous structural/body repairs"
      ]
    },
    "faqs": [
      {
        "question": "Is a cross-member the same as a main frame rail?",
        "answer": "No. A cross-member runs between the longitudinal rails. Identify which area appears affected rather than treating every frame component as the same part."
      },
      {
        "question": "Can a rear impact affect the cargo floor or door opening?",
        "answer": "Connected body supports and openings can be relevant after an impact. Include any floor or door changes with the rear-structure request."
      },
      {
        "question": "Can the repair be chosen from one close-up?",
        "answer": "A close-up may show visible damage, but not the whole connection or adjoining structure. Provide wider context and confirm the inspection needed with the shop."
      }
    ],
    "related": [
      {
        "path": "/Fleet-Truck-Body-Repair",
        "label": "Commercial frame and body repair"
      },
      {
        "path": "/box-truck-repair.html",
        "label": "Box truck body assessment"
      },
      {
        "path": "/Lift-Gate-Repair",
        "label": "Rear sill and liftgate bodywork"
      }
    ],
    "sourceUrls": [
      "https://www.motorcitytruckcollision.com/how-cross-members-get-damaged-in-truck-collisions",
      "https://www.motorcitytruckcollision.com/Fleet-Truck-Body-Repair"
    ],
    "preservedFacts": [
      "Cross-members connect main rails and supporting components.",
      "Rear/side/understructure impacts and attachment damage are relevant.",
      "Floor, body-mount and opening changes can accompany damage."
    ],
    "existingAliases": [
      "/259.html"
    ]
  },
  {
    "id": "trailer-tongue-after-impact-guide",
    "path": "/how-to-inspect-a-trailer-tongue-after-impact",
    "kind": "guide",
    "navLabel": "Trailer Tongue After an Impact",
    "eyebrow": "Visible conditions and inspection planning",
    "title": "Trailer Tongue Assessment After an Impact | Motor City Truck Collision",
    "description": "Record visible tongue, coupler and attachment changes after an impact. Confirm professional trailer inspection scope with the appropriate service provider.",
    "h1": "Trailer Tongue Damage After an Impact",
    "intro": "A trailer-tongue impact can affect the coupler and adjoining attachments. Record visible conditions and explain what happened to a professional trailer service provider. Appearance alone does not establish towing suitability.",
    "answer": {
      "question": "What should I report after an impact to a trailer tongue?",
      "text": "Describe visible bends or kinks, cracks or changes around joints, gaps at attachments, damaged mounting points and changes in coupler position or fit. Include the impact location and the trailer type. The tongue and coupling assembly require an appropriate professional assessment before repair scope is decided."
    },
    "sections": [
      {
        "heading": "Describe the impact and visible changes",
        "paragraphs": [
          "Explain whether the tongue or coupler contacted a fixed object and what looked different afterward. Include a wider view of the trailer front and close-ups of accessible visible damage.",
          "Report changes around the coupler, jack mount, chain attachment points or surrounding frame. These observations help define the inspection request; they do not identify every affected component."
        ]
      },
      {
        "heading": "Include the trailer's identity and earlier condition",
        "paragraphs": [
          "Provide the manufacturer, trailer type and any available component markings. Mention corrosion, previous repairs or earlier coupling concerns so the provider has context for the assessment.",
          "Describe operating changes separately; a symptom alone does not identify the repair or establish towing suitability."
        ]
      },
      {
        "heading": "Confirm the correct inspection provider",
        "paragraphs": [
          "Tongue, coupler and attachment concerns can involve structural or mechanical work beyond exterior trailer-body repairs. Contact Motor City Trailer Repair with the details and confirm the inspection scope for your trailer.",
          "If the enclosure, doors or finish are also damaged, identify that separate body-repair concern for Motor City Truck Collision."
        ]
      }
    ],
    "checklist": {
      "heading": "Information for a trailer inspection inquiry",
      "items": [
        "Trailer manufacturer, type and component markings",
        "Where and when the impact occurred",
        "Wide views and accessible photos of visible changes",
        "Affected coupler, jack or attachment areas",
        "Previous repairs, corrosion and any separate body damage"
      ]
    },
    "faqs": [
      {
        "question": "Does a small visible bend describe the whole problem?",
        "answer": "No. Visible appearance alone does not establish the condition of the tongue, connections or adjoining frame. Confirm the inspection required with the appropriate provider."
      },
      {
        "question": "How is towing suitability assessed after a tongue impact?",
        "answer": "A qualified trailer service provider needs to assess the actual trailer, tongue, coupler and adjoining attachments. Include visible changes and impact details when arranging that assessment."
      },
      {
        "question": "Who should I contact about the tongue or coupling assembly?",
        "answer": "Contact Motor City Trailer Repair and describe the trailer and impact. Confirm whether it can inspect that assembly and arrange the appropriate service or referral."
      }
    ],
    "note": {
      "heading": "Confirm trailer inspection scope",
      "text": "Ask Motor City Trailer Repair about current tongue and coupler inspection capability before scheduling.",
      "href": "https://www.motorcitytrailerrepair.com/",
      "linkLabel": "Contact Motor City Trailer Repair"
    },
    "related": [
      {
        "path": "/Trailer-Repair",
        "label": "Trailer exterior bodywork"
      },
      {
        "path": "/Top-Trailer-Maintenance-Services-That-Must-Be-Done-Regularly",
        "label": "Trailer maintenance planning"
      },
      {
        "path": "/Enclosed-Trailer-Repair",
        "label": "Enclosed trailer body repair"
      }
    ],
    "sourceUrls": [
      "https://www.motorcitytruckcollision.com/how-to-inspect-a-trailer-tongue-after-impact",
      "https://www.motorcitytruckcollision.com/Trailer-Repair"
    ],
    "preservedFacts": [
      "Visible tongue, coupler, mounting and attachment changes after impacts.",
      "Trailer/component identity and previous condition matter.",
      "Professional inspection scope is confirmed with the appropriate trailer provider.",
      "Exterior trailer body damage and tongue/coupling inspection are distinct scopes."
    ],
    "existingAliases": [
      "/318.html"
    ]
  }
]
